import { useQuery, useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Card, Table, Typography, Select, Space, Button, DatePicker, Input, message } from '../../ui';
import { Download } from 'lucide-react';
import dayjs from 'dayjs';
import { disableFromDate, disableToDate } from '../../lib/dateRange';
import { request, api } from '../../api/client';

export function ReportsPage() {
  const [code, setCode] = useState<string | undefined>();
  const [from, setFrom] = useState<string>(new Date(Date.now() - 30 * 86400000).toISOString().substring(0, 10));
  const [to, setTo] = useState<string>(new Date().toISOString().substring(0, 10));
  // Extra per-report filters (warehouse / batch / status / verification) declared by the catalog.
  const [filters, setFilters] = useState<Record<string, any>>({});

  const { data: catalog } = useQuery({
    queryKey: ['reports-catalog'],
    queryFn: () => request<any[]>({ url: '/reports' }),
  });

  const run = useMutation({
    mutationFn: () => request<any>({ url: `/reports/${code}/run`, method: 'POST', data: { from, to, ...filters } }),
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Report failed'),
  });

  const validRange = !from || !to || from <= to;

  const downloadXlsx = async () => {
    try {
      const r = await api.post(`/reports/${code}/run.xlsx`, { from, to, ...filters }, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${code}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      message.error('Download failed');
    }
  };

  const selected = catalog?.find((r) => r.code === code);

  // Switching reports clears stale filters + results (columns differ per report).
  const onPickReport = (c: string) => {
    setCode(c);
    setFilters({});
    run.reset();
  };

  return (
    <div>
      <Typography.Title level={4}>Reports</Typography.Title>
      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            placeholder="Pick a report…"
            style={{ width: 360 }}
            value={code}
            onChange={onPickReport}
            options={(catalog ?? []).map((r) => ({ value: r.code, label: `[${r.category}] ${r.name}` }))}
          />
          <DatePicker value={from ? dayjs(from) : null} onChange={(d) => setFrom(d?.format('YYYY-MM-DD') ?? '')} placeholder="From" disabledDate={disableFromDate(to)} />
          <DatePicker value={to ? dayjs(to) : null} onChange={(d) => setTo(d?.format('YYYY-MM-DD') ?? '')} placeholder="To" disabledDate={disableToDate(from)} />
          {(selected?.filters ?? []).map((f: any) =>
            f.type === 'text' ? (
              <Input
                key={f.key}
                allowClear
                placeholder={f.label}
                style={{ width: 160 }}
                value={filters[f.key] ?? ''}
                onChange={(e) => setFilters((s) => ({ ...s, [f.key]: e.target.value || undefined }))}
              />
            ) : (
              <Select
                key={f.key}
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={f.label}
                style={{ width: 220 }}
                value={filters[f.key]}
                onChange={(v) => setFilters((s) => ({ ...s, [f.key]: v }))}
                options={f.options ?? []}
              />
            ),
          )}
          <Button variant="primary" disabled={!code || !validRange} onClick={() => run.mutate()} loading={run.isPending}>Run</Button>
          <Button variant="default" disabled={!code || !validRange} icon={<Download size={16} />} onClick={downloadXlsx}>Excel</Button>
        </Space>

        {run.data && selected && (
          <Table
            rowKey={(_, i) => String(i)}
            dataSource={run.data.rows ?? []}
            columns={(selected.columns ?? []).map((c: any) => ({ title: c.header, dataIndex: c.key }))}
            size="small"
            pagination={{ pageSize: 25 }}
          />
        )}
      </Card>
    </div>
  );
}
